/* eslint-disable max-lines -- was already at the limit; the delegation-mode-control wrapper pushed it 2 over */
import { useContext, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { AlertTriangle, Check, Mail, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { AdminFocusedSurfaceContext } from "@/features/admin-dashboard/admin-focused-surface-context"
import { useRegisterAdminDirty } from "@/features/admin-dashboard/admin-unsaved-changes-context"
import {
  useFlowEmailBrandingQuery,
  useFlowEmailConfigQuery,
  useTestFlowEmailConfigMutation,
  useUpdateFlowEmailBrandingMutation,
  useUpdateFlowEmailConfigMutation,
  useVerifyFlowEmailConfigMutation,
} from "@/features/admin-dashboard/hooks/admin-queries"

import { AdminSettingsLoadState } from "./admin-settings-load-state"
import { DelegationModeControl } from "./delegation-mode-control"
import { parseEmailList } from "./email-list"
import { EmailListField } from "./email-list-field"
import { isValidSecretRefInput, toSecretRef } from "./secret-ref-utils"

type VerifyResult = { secretValid: boolean; tokenValid: boolean; error?: string }

function CheckRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-xs">
      {ok ? (
        <Check className="text-success size-3.5" />
      ) : (
        <X className="text-destructive size-3.5" />
      )}
      <span className={ok ? "text-success" : "text-destructive"}>{label}</span>
    </div>
  )
}

/**
 * Admin control for the brand shown on recipient-facing flow emails (the Email
 * node and completion notifications). Name overrides the "Hello AI" wordmark
 * (e.g. "NOA"); logo URL is a hosted https PNG/JPEG or an inline data:image/...
 * URI (capped at 256 KB). Empty fields fall back to "Hello AI" with no logo.
 */
export function FlowEmailBrandingPanel() {
  const { t } = useTranslation()
  const isFocusedSurface = useContext(AdminFocusedSurfaceContext)
  const { data, isLoading, isFetching, refetch } = useFlowEmailBrandingQuery()
  const {
    data: config,
    isLoading: configLoading,
    isFetching: configFetching,
    refetch: refetchConfig,
  } = useFlowEmailConfigQuery()
  const update = useUpdateFlowEmailBrandingMutation()
  const updateConfig = useUpdateFlowEmailConfigMutation()
  const testConfig = useTestFlowEmailConfigMutation()
  const verifyConfig = useVerifyFlowEmailConfigMutation()

  const [name, setName] = useState("")
  const [logoUrl, setLogoUrl] = useState("")
  const [enabled, setEnabled] = useState(true)
  const [tenantId, setTenantId] = useState("")
  const [clientId, setClientId] = useState("")
  const [clientSecret, setClientSecret] = useState("")
  const [senderEmail, setSenderEmail] = useState("")
  const [recipients, setRecipients] = useState("")
  const [recipientAllowlist, setRecipientAllowlist] = useState("")
  const [testRecipient, setTestRecipient] = useState("")
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null)

  useEffect(() => {
    if (data) {
      setName(data.name ?? "")
      setLogoUrl(data.logo_url ?? "")
    }
  }, [data])

  useEffect(() => {
    if (config) {
      setEnabled(config.enabled)
      setTenantId(config.tenant_id)
      setClientId(config.client_id)
      setClientSecret(config.client_secret ?? "")
      setSenderEmail(config.sender_email)
      setRecipients(config.recipients.join("\n"))
      setRecipientAllowlist(config.recipient_allowlist.join("\n"))
      setTestRecipient(config.recipients[0] || "")
      setVerifyResult(null)
    }
  }, [config])

  const dirty = (data?.name ?? "") !== name.trim() || (data?.logo_url ?? "") !== logoUrl.trim()
  const recipientList = parseEmailList(recipients)
  const recipientAllowlistList = parseEmailList(recipientAllowlist)
  const configDirty =
    (config?.enabled ?? true) !== enabled ||
    (config?.tenant_id ?? "") !== tenantId.trim() ||
    (config?.client_id ?? "") !== clientId.trim() ||
    (config?.client_secret ?? "") !== clientSecret.trim() ||
    (config?.sender_email ?? "") !== senderEmail.trim() ||
    (config?.recipients ?? []).join("\n") !== recipientList.join("\n") ||
    (config?.recipient_allowlist ?? []).join("\n") !== recipientAllowlistList.join("\n")
  const hasUnsavedChanges = dirty || configDirty
  useRegisterAdminDirty("flow-email-branding", hasUnsavedChanges)

  // The backend stores (and verifies) a Key Vault REFERENCE, so the typed name is
  // turned into "@kv:<name>" here; an unusable name is refused before any request.
  const secretRefValid = isValidSecretRefInput(clientSecret)

  const markConfigDirty = () => {
    setVerifyResult(null)
  }

  const handleSave = async () => {
    try {
      await update.mutateAsync({ name: name.trim() || null, logo_url: logoUrl.trim() || null })
      toast.success(
        t("adminDashboard.flowEmailBranding.saved", { defaultValue: "Email branding updated" })
      )
    } catch {
      toast.error(
        t("adminDashboard.flowEmailBranding.saveFailed", {
          defaultValue: "Could not update email branding",
        })
      )
    }
  }

  const handleConfigSave = async () => {
    if (!secretRefValid) return
    try {
      await updateConfig.mutateAsync({
        enabled,
        tenant_id: tenantId.trim(),
        client_id: clientId.trim(),
        client_secret: toSecretRef(clientSecret),
        sender_email: senderEmail.trim(),
        recipients: recipientList,
        recipient_allowlist: recipientAllowlistList,
      })
      toast.success(
        t("adminDashboard.flowEmailBranding.configSaved", {
          defaultValue: "Flow email settings updated",
        })
      )
    } catch {
      toast.error(
        t("adminDashboard.flowEmailBranding.configSaveFailed", {
          defaultValue: "Could not update Flow email settings",
        })
      )
    }
  }

  const handleTestSend = async () => {
    try {
      await testConfig.mutateAsync(testRecipient.trim())
      toast.success(
        t("adminDashboard.flowEmailBranding.testSent", { defaultValue: "Test email sent" })
      )
    } catch {
      toast.error(
        t("adminDashboard.flowEmailBranding.testFailed", {
          defaultValue: "Could not send test email",
        })
      )
    }
  }

  const handleVerify = async () => {
    if (!secretRefValid) return
    try {
      const result = await verifyConfig.mutateAsync({
        tenant_id: tenantId.trim() || null,
        client_id: clientId.trim() || null,
        client_secret: toSecretRef(clientSecret),
      })
      setVerifyResult(result)
    } catch {
      setVerifyResult({
        secretValid: false,
        tokenValid: false,
        error: t("adminDashboard.flowEmailBranding.verifyFailed", {
          defaultValue: "Verification request failed",
        }),
      })
    }
  }

  return (
    <Card data-admin-dirty={hasUnsavedChanges ? "true" : undefined}>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          {!isFocusedSurface ? (
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="size-4" />
              {t("adminDashboard.flowEmailBranding.title", { defaultValue: "Flow email branding" })}
            </CardTitle>
          ) : null}
          <DelegationModeControl capability="flows" />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading || configLoading || !data || !config ? (
          <AdminSettingsLoadState
            loading={isLoading || configLoading}
            retrying={isFetching || configFetching}
            onRetry={() => {
              if (!data) void refetch()
              if (!config) void refetchConfig()
            }}
          />
        ) : (
          <div className="grid gap-4">
            <p className="text-muted-foreground text-xs">
              {t("adminDashboard.flowEmailBranding.help", {
                defaultValue:
                  "Branding for emails sent by flows (Email node + completion notifications). Leave blank to use the default.",
              })}
            </p>
            <div className="rounded-md border p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label className="text-sm" htmlFor="flow-email-enabled">
                    {t("adminDashboard.flowEmailBranding.enabledLabel", {
                      defaultValue: "Use dedicated Flow email sender",
                    })}
                  </Label>
                  <p className="text-muted-foreground mt-1 text-xs">
                    {t("adminDashboard.flowEmailBranding.enabledHelp", {
                      defaultValue:
                        "Uses a dedicated Flow mail app, separate from alert emails. Configure credentials here with a Key Vault secret reference, or leave them blank to use FLOW_EMAIL_* App Config values.",
                    })}
                  </p>
                </div>
                <Switch
                  checked={enabled}
                  id="flow-email-enabled"
                  onCheckedChange={(value) => {
                    setEnabled(value)
                    markConfigDirty()
                  }}
                />
              </div>
              <div className="mt-3 grid gap-1 text-xs">
                <span>
                  {t("adminDashboard.flowEmailBranding.credentialsStatus", {
                    defaultValue: "Credentials",
                  })}
                  {": "}
                  {config.credentials_configured
                    ? t("adminDashboard.flowEmailBranding.configured", {
                        defaultValue: "Configured",
                      })
                    : t("adminDashboard.flowEmailBranding.notConfigured", {
                        defaultValue: "Not configured",
                      })}
                  {config.credential_source
                    ? ` (${
                        config.credential_source === "admin"
                          ? t("adminDashboard.flowEmailBranding.adminSource", {
                              defaultValue: "Admin Dashboard / Key Vault",
                            })
                          : t("adminDashboard.flowEmailBranding.appConfigSource", {
                              defaultValue: "App Config / Key Vault",
                            })
                      })`
                    : ""}
                </span>
                <span>
                  {t("adminDashboard.flowEmailBranding.secretStatus", {
                    defaultValue: "App secret",
                  })}
                  {": "}
                  {config.secret_configured
                    ? t("adminDashboard.flowEmailBranding.configured", {
                        defaultValue: "Configured",
                      })
                    : t("adminDashboard.flowEmailBranding.notConfigured", {
                        defaultValue: "Not configured",
                      })}
                </span>
                <span>
                  {t("adminDashboard.flowEmailBranding.channelStatus", {
                    defaultValue: "Channel",
                  })}
                  {": "}
                  {config.channel_configured
                    ? t("adminDashboard.flowEmailBranding.ready", { defaultValue: "Ready" })
                    : t("adminDashboard.flowEmailBranding.notReady", {
                        defaultValue: "Not ready",
                      })}
                </span>
                <span className="text-muted-foreground">
                  {t("adminDashboard.flowEmailBranding.credentialsHelp", {
                    defaultValue:
                      "The secret value must live in Key Vault. Enter only the Key Vault secret name; saved references are masked.",
                  })}
                </span>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label className="text-sm" htmlFor="flow-email-tenant-id">
                  {t("adminDashboard.flowEmailBranding.tenantIdLabel", {
                    defaultValue: "Tenant ID",
                  })}
                </Label>
                <Input
                  autoComplete="off"
                  className="font-mono text-sm"
                  id="flow-email-tenant-id"
                  placeholder="00000000-0000-0000-0000-000000000000"
                  value={tenantId}
                  onChange={(e) => {
                    setTenantId(e.target.value)
                    markConfigDirty()
                  }}
                />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-sm" htmlFor="flow-email-client-id">
                  {t("adminDashboard.flowEmailBranding.clientIdLabel", {
                    defaultValue: "Application (client) ID",
                  })}
                </Label>
                <Input
                  autoComplete="off"
                  className="font-mono text-sm"
                  id="flow-email-client-id"
                  placeholder="00000000-0000-0000-0000-000000000000"
                  value={clientId}
                  onChange={(e) => {
                    setClientId(e.target.value)
                    markConfigDirty()
                  }}
                />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-sm" htmlFor="flow-email-client-secret">
                {t("adminDashboard.flowEmailBranding.clientSecretLabel", {
                  defaultValue: "Client secret (Key Vault secret name)",
                })}
              </Label>
              <Input
                autoComplete="off"
                className="font-mono text-sm"
                id="flow-email-client-secret"
                placeholder="flow-mail-secret"
                value={clientSecret}
                onChange={(e) => {
                  setClientSecret(e.target.value)
                  markConfigDirty()
                }}
              />
              <p className="text-muted-foreground text-xs">
                {t("adminDashboard.flowEmailBranding.clientSecretHelp", {
                  defaultValue:
                    "Enter the Key Vault secret name, not the secret value — it is stored as a Key Vault reference (@kv:<name>). Leave the mask to keep the current reference; clear it to remove admin credentials.",
                })}
              </p>
              {/* Say why a name cannot become a reference before the save is tried:
                  the backend answers anything but @kv:<name> with a 400. */}
              {!secretRefValid && (
                <p className="text-destructive text-xs" data-testid="flow-email-secret-error">
                  {t("adminDashboard.secretRef.invalidName", {
                    defaultValue:
                      "Use letters, digits and hyphens only — enter the Key Vault secret name, not its value.",
                  })}
                </p>
              )}
            </div>
            {!config.channel_configured && enabled ? (
              <div className="border-destructive/30 bg-destructive/10 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <span>
                  {t("adminDashboard.flowEmailBranding.notConfiguredWarning", {
                    defaultValue:
                      "Flow mail sender is not ready. Complete and verify the app credentials plus sender mailbox, or leave admin credentials blank to use FLOW_EMAIL_* App Config values.",
                  })}
                </span>
              </div>
            ) : null}
            <div className="flex flex-wrap items-center gap-3">
              <Button
                disabled={verifyConfig.isPending || !secretRefValid}
                size="sm"
                variant="outline"
                onClick={() => void handleVerify()}
              >
                {verifyConfig.isPending ? <Spinner className="mr-2 size-4" /> : null}
                {t("adminDashboard.flowEmailBranding.verify", { defaultValue: "Verify" })}
              </Button>
              {verifyResult ? (
                <div className="grid gap-1">
                  <CheckRow
                    ok={verifyResult.secretValid}
                    label={
                      verifyResult.secretValid
                        ? t("adminDashboard.flowEmailBranding.secretOk", {
                            defaultValue: "Key Vault secret found",
                          })
                        : t("adminDashboard.flowEmailBranding.secretBad", {
                            defaultValue: "Key Vault secret not found",
                          })
                    }
                  />
                  <CheckRow
                    ok={verifyResult.tokenValid}
                    label={
                      verifyResult.tokenValid
                        ? t("adminDashboard.flowEmailBranding.tokenOk", {
                            defaultValue: "Authentication succeeded",
                          })
                        : t("adminDashboard.flowEmailBranding.tokenBad", {
                            defaultValue: "Authentication failed",
                          })
                    }
                  />
                  {verifyResult.error ? (
                    <p className="text-destructive text-xs">{verifyResult.error}</p>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="grid gap-1.5">
              <Label className="text-sm" htmlFor="flow-email-sender">
                {t("adminDashboard.flowEmailBranding.senderLabel", {
                  defaultValue: "Sender mailbox",
                })}
              </Label>
              <Input
                id="flow-email-sender"
                placeholder="flows@example.com"
                value={senderEmail}
                onChange={(e) => {
                  setSenderEmail(e.target.value)
                  markConfigDirty()
                }}
              />
            </div>
            <EmailListField
              id="flow-email-recipients"
              placeholder="daniel@x-one.app"
              value={recipients}
              label={t("adminDashboard.flowEmailBranding.defaultRecipientsLabel", {
                defaultValue: "Default test recipients",
              })}
              onChange={(value) => {
                setRecipients(value)
                markConfigDirty()
              }}
            />
            <EmailListField
              id="flow-email-allowlist"
              placeholder={"partner.com\nalice@customer.com"}
              value={recipientAllowlist}
              help={t("adminDashboard.flowEmailBranding.recipientAllowlistHelp", {
                defaultValue:
                  "Flow Email nodes may only send to your own tenant domain plus the addresses or domains listed here. One per line; use a bare domain (partner.com) or a full address.",
              })}
              label={t("adminDashboard.flowEmailBranding.recipientAllowlistLabel", {
                defaultValue: "Allowed external recipients",
              })}
              onChange={(value) => {
                setRecipientAllowlist(value)
                markConfigDirty()
              }}
            />
            <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
              <Input
                placeholder="daniel@x-one.app"
                value={testRecipient}
                aria-label={t("adminDashboard.flowEmailBranding.testRecipient", {
                  defaultValue: "Test recipient",
                })}
                onChange={(e) => setTestRecipient(e.target.value)}
              />
              <Button
                variant="outline"
                disabled={
                  !testRecipient.trim() || testConfig.isPending || !config.channel_configured
                }
                onClick={() => void handleTestSend()}
              >
                {t("adminDashboard.flowEmailBranding.sendTest", { defaultValue: "Send test" })}
              </Button>
            </div>
            <div className="flex justify-end">
              <Button
                disabled={!configDirty || updateConfig.isPending || !secretRefValid}
                onClick={() => void handleConfigSave()}
              >
                {t("common.save", { defaultValue: "Save" })}
              </Button>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-sm" htmlFor="flow-email-brand-name">
                {t("adminDashboard.flowEmailBranding.nameLabel", { defaultValue: "Brand name" })}
              </Label>
              <Input
                id="flow-email-brand-name"
                placeholder="Hello AI"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-sm" htmlFor="flow-email-brand-logo">
                {t("adminDashboard.flowEmailBranding.logoLabel", {
                  defaultValue: "Logo URL (https PNG/JPEG or data:image URI)",
                })}
              </Label>
              <Input
                id="flow-email-brand-logo"
                placeholder="https://example.com/logo.png"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
              />
              <p className="text-muted-foreground text-xs">
                {t("adminDashboard.flowEmailBranding.logoHelp", {
                  defaultValue:
                    "A hosted https raster image or an inline data:image URI (≤256 KB) — SVGs don't render in most email clients. Blank shows the brand name instead.",
                })}
              </p>
            </div>
            <div className="flex justify-end">
              <Button disabled={!dirty || update.isPending} onClick={() => void handleSave()}>
                {t("common.save", { defaultValue: "Save" })}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
